import { darken, rem } from 'polished';
import PropTypes from 'prop-types';
import React from 'react';
import styled, { css, ThemeProvider, withTheme } from 'styled-components';
import Icon from '../Icon';
import Loader from '../Loader';

const customDarken = (amount, color) => {
  if (color) {
    if (color === 'transparent') {
      return `rgba(0, 0, 0, ${amount})`;
    }
    return darken(amount, color);
  }
};

const Wrapper = styled.button`
  position: relative;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  display: inline-flex;
  width: ${({ block }) => (block ? '100%' : 'auto')};
  align-items: center;
  justify-content: center;
  min-height: ${rem(42)};
  outline: 0;
  border: none;
  background: ${({ theme }) => theme.buttonBackgroundColor};
  color: ${({ theme }) => theme.buttonTextColor};
  vertical-align: baseline;
  text-transform: none;
  text-shadow: none;
  font-weight: bold;
  line-height: 1em;
  font-style: normal;
  font-size: ${rem(14)};
  text-decoration: none;
  border-radius: ${({ theme }) => theme.borderRadius};
  user-select: none;
  transition: opacity 0.1s ease, background-color 0.1s ease, color 0.1s ease,
    box-shadow 0.1s ease, background 0.1s ease;
  padding: ${rem(10)} ${rem(20)};
  ${({ disabled }) =>
    disabled
      ? ''
      : css`
          &:hover {
            background: ${({ theme }) =>
              customDarken(0.05, theme.buttonBackgroundColor)};
          }
          &:focus {
            background: ${({ theme }) =>
              customDarken(0.1, theme.buttonBackgroundColor)};
          }
          &:active {
            background: ${({ theme }) =>
              customDarken(0.2, theme.buttonBackgroundColor)};
          }
        `};
`;

const StyledIcon = styled(Icon)`
  margin-right: ${rem(9)};
`;

const StyledLoader = styled(Loader)`
  position: absolute;
  left: 50%;
  top: 50%;
  transition: transform 0.2s, opacity 0.2s;
  transform-origin: left top;
  ${({ loading }) => {
    if (loading) {
      return css`
        opacity: 1;
        transform: scale(1) translate(-50%, -50%);
      `;
    }
    return css`
      opacity: 0;
      transform: scale(0) translate(-50%, -50%);
    `;
  }};
`;

const Content = styled.div`
  transition: opacity 0.2s;
  opacity: ${({ loading }) => (loading ? 0.25 : 1)};
  display: flex;
  align-items: center;
`;

export const Button = props => {
  const {
    theme,
    primary,
    danger,
    success,
    block,
    basic,
    loading,
    disabled,
    type,
    onClick,
  } = props;

  let buttonTheme = {
    buttonBackgroundColor: 'transparent',
    buttonTextColor: theme.color.font.primary,
  };

  // basic states
  if (primary) {
    if (basic) {
      buttonTheme = {
        ...buttonTheme,
        buttonTextColor: theme.color.primary,
      };
    } else {
      buttonTheme = {
        ...buttonTheme,
        buttonBackgroundColor: theme.color.primary,
        buttonTextColor: '#fff',
      };
    }
  } else if (danger) {
    if (basic) {
      buttonTheme = {
        ...buttonTheme,
        buttonTextColor: theme.color.danger,
      };
    } else {
      buttonTheme = {
        ...buttonTheme,
        buttonBackgroundColor: theme.color.danger,
        buttonTextColor: '#fff',
      };
    }
  } else if (success) {
    if (basic) {
      buttonTheme = {
        ...buttonTheme,
        buttonTextColor: theme.color.success,
      };
    } else {
      buttonTheme = {
        ...buttonTheme,
        buttonBackgroundColor: theme.color.success,
        buttonTextColor: '#fff',
      };
    }
  }

  if (disabled) {
    if (basic) {
      buttonTheme = {
        ...buttonTheme,
        buttonTextColor: theme.color.inactive,
      };
    } else {
      buttonTheme = {
        ...buttonTheme,
        buttonBackgroundColor: theme.color.inactive,
        buttonTextColor: '#fff',
      };
    }
  }

  return (
    <ThemeProvider theme={buttonTheme}>
      <Wrapper
        className={props.className}
        block={block}
        disabled={disabled}
        type={type}
        onClick={e => {
          if (!disabled) {
            onClick(e);
          }
        }}
      >
        <Content loading={loading}>
          {props.icon && (
            <StyledIcon
              width={24}
              height={24}
              name={props.icon}
              color={buttonTheme.buttonTextColor}
            />
          )}
          {props.children}
        </Content>
        <StyledLoader
          size="l"
          loading={loading}
          color={buttonTheme.buttonTextColor}
        />
      </Wrapper>
    </ThemeProvider>
  );
};

Button.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  primary: PropTypes.bool,
  danger: PropTypes.bool,
  success: PropTypes.bool,
  block: PropTypes.bool,
  basic: PropTypes.bool,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  theme: PropTypes.shape({
    color: PropTypes.shape({}).isRequired,
  }).isRequired,
  icon: PropTypes.string,
  type: PropTypes.string,
  onClick: PropTypes.func,
};
Button.defaultProps = {
  className: '',
  children: null,
  primary: false,
  danger: false,
  success: false,
  block: false,
  basic: false,
  loading: false,
  disabled: false,
  icon: null,
  type: 'button',
  onClick: () => {},
};

export default withTheme(Button);



// WEBPACK FOOTER //
// ./src/components/Button/index.js